/*
 * ÉCHARPEZ — Theme JavaScript
 * Quiet Luxury. Italian Craftsmanship.
 */

(function() {
  'use strict';

  /* ========== Scroll Reveal Animation ========== */
  const initScrollReveal = () => {
    const reveals = document.querySelectorAll('.reveal, .fade-up');
    if (!reveals.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -40px 0px'
    });

    reveals.forEach(el => observer.observe(el));
  };

  /* ========== Mobile Menu ========== */
  const initMobileMenu = () => {
    const toggle = document.getElementById('mobile-menu-toggle');
    const menu = document.getElementById('mobile-menu');
    const close = document.getElementById('mobile-menu-close');

    if (!toggle || !menu) return;

    // Helper to safely get overlay (it may load after this script)
    const getOverlay = () => document.getElementById('overlay');

    const openMenu = () => {
      menu.classList.add('is-active');
      const overlay = getOverlay();
      if (overlay) overlay.classList.add('is-active');
      document.body.classList.add('drawer-open');
      toggle.setAttribute('aria-expanded', 'true');
    };

    const closeMenu = () => {
      menu.classList.remove('is-active');
      const overlay = getOverlay();
      if (overlay) overlay.classList.remove('is-active');
      document.body.classList.remove('drawer-open');
      toggle.setAttribute('aria-expanded', 'false');
    };

    toggle.addEventListener('click', openMenu);
    if (close) close.addEventListener('click', closeMenu);

    // Close overlay on click (use event delegation since overlay is late-loaded)
    document.addEventListener('click', (e) => {
      const overlay = getOverlay();
      if (e.target === overlay && menu.classList.contains('is-active')) {
        closeMenu();
      }
    });

    // Close menu when an actual nav link is clicked (excluding accordion buttons)
    const mobileLinks = menu.querySelectorAll('a.mobile-menu__link, .mobile-menu__sublink');
    mobileLinks.forEach(link => {
      link.addEventListener('click', () => {
        closeMenu();
      });
    });
  };

  /* ========== Cart Drawer ========== */
  const initCartDrawer = () => {
    const drawer = document.getElementById('cart-drawer');
    const openBtns = document.querySelectorAll('[data-cart-toggle]');
    const closeBtn = document.getElementById('cart-drawer-close');

    if (!drawer) return;

    const getOverlay = () => document.getElementById('overlay');

    const openDrawer = () => {
      drawer.classList.add('is-active');
      const overlay = getOverlay();
      if (overlay) overlay.classList.add('is-active');
      document.body.classList.add('drawer-open');
    };

    const closeDrawer = () => {
      drawer.classList.remove('is-active');
      const overlay = getOverlay();
      if (overlay) overlay.classList.remove('is-active');
      document.body.classList.remove('drawer-open');
    };

    openBtns.forEach(btn => btn.addEventListener('click', (e) => {
      e.preventDefault();
      openDrawer();
    }));

    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);

    // Close on overlay click (event delegation)
    document.addEventListener('click', (e) => {
      const overlay = getOverlay();
      if (e.target === overlay && drawer.classList.contains('is-active')) {
        closeDrawer();
      }
    });

    // Expose globally for AJAX add-to-cart
    window.CartDrawer = {
      open: openDrawer,
      close: closeDrawer,
      refresh: async () => {
        try {
          const res = await fetch('/?sections=cart-drawer');
          const data = await res.json();
          if (data['cart-drawer']) {
            const temp = document.createElement('div');
            temp.innerHTML = data['cart-drawer'];
            const newContent = temp.querySelector('.cart-drawer__inner');
            const currentContent = drawer.querySelector('.cart-drawer__inner');
            if (newContent && currentContent) {
              currentContent.innerHTML = newContent.innerHTML;
            }
          }
        } catch (e) {
          console.error('Cart refresh failed:', e);
        }
      }
    };
  };

  /* ========== Sticky Header ========== */
  const initStickyHeader = () => {
    const header = document.querySelector('.site-header');
    if (!header) return;

    let lastScroll = 0;
    const headerHeight = header.offsetHeight;

    window.addEventListener('scroll', () => {
      const currentScroll = window.pageYOffset;

      if (currentScroll > headerHeight) {
        header.classList.add('is-scrolled');
      } else {
        header.classList.remove('is-scrolled');
      }

      if (currentScroll > lastScroll && currentScroll > headerHeight * 2) {
        header.classList.add('is-hidden');
      } else {
        header.classList.remove('is-hidden');
      }

      lastScroll = currentScroll;
    }, { passive: true });
  };

  /* ========== AJAX Add to Cart ========== */
  const initAddToCart = () => {
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('form[action="/cart/add"]');
      if (!form) return;

      e.preventDefault();

      const submitBtn = form.querySelector('[type="submit"]');
      const btnText = submitBtn ? submitBtn.textContent : '';

      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Adding...';
      }

      try {
        const formData = new FormData(form);
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        if (res.ok) {
          if (window.CartDrawer) {
            await window.CartDrawer.refresh();
            window.CartDrawer.open();
          }

          // Update cart count
          const cartRes = await fetch('/cart.js');
          const cart = await cartRes.json();
          const countEls = document.querySelectorAll('[data-cart-count]');
          countEls.forEach(el => {
            el.textContent = cart.item_count;
            if (cart.item_count > 0) {
              el.classList.add('has-items');
            }
          });
        } else {
          const error = await res.json();
          alert(error.description || 'Could not add item to cart');
        }
      } catch (err) {
        console.error('Add to cart error:', err);
      } finally {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = btnText;
        }
      }
    });
  };

  /* ========== Quantity Buttons ========== */
  const initQuantityButtons = () => {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-qty-btn]');
      if (!btn) return;

      const wrapper = btn.closest('[data-qty-wrapper]');
      const input = wrapper.querySelector('[data-qty-input]');
      if (!input) return;

      let val = parseInt(input.value) || 1;
      const action = btn.dataset.qtyBtn;

      if (action === 'minus' && val > 1) {
        input.value = val - 1;
      } else if (action === 'plus') {
        input.value = val + 1;
      }

      input.dispatchEvent(new Event('change'));
    });
  };

  /* ========== Init Everything ========== */
  const init = () => {
    initScrollReveal();
    initMobileMenu();
    initCartDrawer();
    initStickyHeader();
    initAddToCart();
    initQuantityButtons();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
